import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import DownloadButton from './DownloadButton';
import WaitlistModal from './WaitlistModal';
import { useTranslation } from 'react-i18next';

// ======================= HERO SECTION =======================
const HeroSection = styled.section`
  height: 100vh;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

// ======================= ВИДЕО-ФОН (СКРЫТ НА МОБИЛЕ) =======================
const VideoBG = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.3;
  z-index: 0;

  /* На мобильных — убираем все контроллы и делаем "невидимым" для пользователя */
  @media (max-width: 768px) {
    /* Полностью скрываем плеер */
    pointer-events: none;
    -webkit-appearance: none;
    
    /* Убираем все элементы управления */
    &::-webkit-media-controls,
    &::-webkit-media-controls-panel,
    &::-webkit-media-controls-play-button,
    &::-webkit-media-controls-volume-slider,
    &::-webkit-media-controls-mute-button,
    &::-webkit-media-controls-timeline,
    &::-webkit-media-controls-current-time-display,
    &::-webkit-media-controls-time-remaining-display,
    &::-webkit-media-controls-seek-back-button,
    &::-webkit-media-controls-seek-forward-button,
    &::-webkit-media-controls-fullscreen-button,
    &::-webkit-media-controls-rewind-button,
    &::-webkit-media-controls-return-to-realtime-button,
    &::-webkit-media-controls-toggle-closed-captions-button {
      display: none !important;
      opacity: 0 !important;
      pointer-events: none !important;
      height: 0 !important;
      width: 0 !important;
    }

    /* Дополнительно: скрываем через атрибуты */
    playsinline
    controls={false}
    disablePictureInPicture
    controlsList="nodownload nofullscreen noremoteplayback"
  }
`;

const GradientTitle = styled.h1`
  font-size: clamp(3rem, 8vw, 6rem);
  margin: 0;
  letter-spacing: -0.03em;
  font-weight: 900;
  background: linear-gradient(90deg, #cfd3d6, #8f9ba6, #e5e7ea, #9ca3af);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: titaniumShift 14s ease infinite;

  @keyframes titaniumShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

// ======================= ПЕЧАТАЮЩИЙ ТЕКСТ =======================
const TypingText = ({ phrases, speed = 90, pause = 3000 }) => {
  const [text, setText] = useState('');
  const [index, setIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  React.useEffect(() => {
    const current = phrases[index % phrases.length];
    const typingSpeed = deleting ? speed / 2 : speed;

    const timer = setTimeout(() => {
      if (!deleting) {
        setText(current.slice(0, text.length + 1));
        if (text.length + 1 === current.length) {
          setTimeout(() => setDeleting(true), pause);
        }
      } else {
        setText(text.slice(0, -1));
        if (text.length === 0) {
          setDeleting(false);
          setIndex(prev => prev + 1);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [text, deleting, index, phrases, speed, pause]);

  return (
    <p style={{ 
      fontSize: 'clamp(1.3rem, 3vw, 2rem)', 
      margin: '1rem 0 2rem', 
      opacity: 0.9 
    }}>
      {text}
      <span style={{ opacity: 0.5 }}>|</span>
    </p>
  );
};

// ======================= HERO =======================
const Hero = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const typingPhrases = [
    t('hero_typing_1'),
    t('hero_typing_2'),
    t('hero_typing_3')
  ];

  return (
    <HeroSection>
      {/* ВИДЕО-ФОН — АВТОПЛЕЙ, БЕЗ УПРАВЛЕНИЯ НА МОБИЛЕ */}
      <VideoBG
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        // Эти атрибуты усиливают "невидимость" плеера на мобиле
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        // На мобиле — вообще без controls
        controls={false}
      >
        <source src="/nebula.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </VideoBG>

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.3 }}
        style={{ 
          zIndex: 2, 
          maxWidth: '800px', 
          padding: '0 2rem',
          position: 'relative'
        }}
      >
        <GradientTitle>{t('hero_title')}</GradientTitle>

        <TypingText phrases={typingPhrases} />

        <p style={{ 
          opacity: 0.8, 
          marginBottom: '2.5rem', 
          fontWeight: 200,
          fontSize: '18px',
          lineHeight: 1.6
        }}>
          {t('hero_subtitle')}
        </p>

        <DownloadButton onClick={() => setShowModal(true)}>
          {t('try_plano')}
        </DownloadButton>
      </motion.div>

      {showModal && <WaitlistModal onClose={() => setShowModal(false)} />}
    </HeroSection>
  );
};

export default Hero;